"use client";

import { CheckCircle2 } from "lucide-react";
import { HeartIcon, TriangleRight } from "@/components/icons/assets";
import { AddToCart } from "@/components/cart/add-to-cart";

interface ProductInfoProps {
  product: {
    name: string;
    price: string;
    rating: number;
    reviewCount: number;
    shortDescription: string;
    carFit: {
      model: string;
      plate: string;
    };
    specs: {
      condition: string;
      boltPattern: string;
      frontSpecs: string;
      rearSpecs: string;
    };
  };
  productId: string;
  productStatus: boolean;
  configurableIndex: any[];
  configurableVariants: any[];
}

export default function ProductInfo({ 
  product, 
  productId, 
  productStatus,
  configurableIndex,
  configurableVariants
}: ProductInfoProps) {
  return (
    <div className="flex flex-col h-full justify-between">
      <nav className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
        <span className="font-plus-jakarta text-neutral-300 font-bold">Home</span>
        <TriangleRight className="h-2.5 text-neutral-300" />
        <span className="font-plus-jakarta text-neutral-300 font-bold">Shop Wheel</span>
        <TriangleRight className="h-2.5 text-neutral-300" />
        <span className="font-plus-jakarta font-bold text-neutral-900">{product.name}</span>
      </nav>

      <div>
        <div className="flex items-start justify-between gap-3 sm:gap-4 mb-2">
          <h1 className="font-plus-jakarta text-xl sm:text-2xl md:text-3xl font-bold text-neutral-900">{product.name}</h1>
          <button className="w-9 h-9 sm:w-10 sm:h-10 bg-background-100 rounded-full cursor-pointer flex items-center justify-center border border-stroke flex-shrink-0">
            <HeartIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
          <div className="flex items-center gap-0.5 sm:gap-1">
            {[...Array(5)].map((_, i) => (
              <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-warning fill-warning" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <span className="font-plus-jakarta text-neutral-300 font-medium text-xs sm:text-sm">({product.reviewCount} Reviews)</span>
        </div>

        <div className="flex flex-wrap items-baseline gap-2 sm:gap-3 mb-4 sm:mb-5">
          <span className="font-plus-jakarta text-2xl sm:text-3xl md:text-4xl font-bold text-primary-500">{product.price}</span>
          <span className="font-plus-jakarta text-neutral-300 text-xs sm:text-sm md:text-base">(Set of 4 wheels without tires)</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
          <div className="flex items-center gap-1.5 sm:gap-2 bg-primary-900 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="font-plus-jakarta text-xs sm:text-sm font-medium">Fits your car</span>
            <span className="font-plus-jakarta text-xs sm:text-sm">{product.carFit.model}</span>
          </div>
          <div className="border border-stroke px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg">
            <span className="font-plus-jakarta text-xs sm:text-sm font-bold text-neutral-900">{product.carFit.plate}</span>
          </div>
        </div>

        <div className="bg-background-50 rounded-lg sm:rounded-xl py-3 sm:py-4 px-3 sm:px-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="text-left">
              <p className="font-plus-jakarta text-xs sm:text-sm text-neutral-300 mb-0.5 sm:mb-1">Condition</p>
              <p className="font-plus-jakarta font-bold text-sm sm:text-base text-neutral-900">{product.specs.condition}</p>
            </div>
            <div className="text-left">
              <p className="font-plus-jakarta text-xs sm:text-sm text-neutral-300 mb-0.5 sm:mb-1">Bolt pattern</p>
              <p className="font-plus-jakarta font-bold text-sm sm:text-base text-neutral-900">{product.specs.boltPattern}</p>
            </div>
            <div className="text-left">
              <p className="font-plus-jakarta text-xs sm:text-sm text-neutral-300 mb-0.5 sm:mb-1">Front specs</p>
              <p className="font-plus-jakarta font-bold text-sm sm:text-base text-neutral-900">{product.specs.frontSpecs}</p>
            </div>
            <div className="text-left">
              <p className="font-plus-jakarta text-xs sm:text-sm text-neutral-300 mb-0.5 sm:mb-1">Rear specs</p>
              <p className="font-plus-jakarta font-bold text-sm sm:text-base text-neutral-900">{product.specs.rearSpecs}</p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <p className="font-plus-jakarta text-neutral-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base font-medium">{product.shortDescription}</p>
          <AddToCart
            availableForSale={productStatus}
            index={configurableIndex}
            productId={productId}
            variants={configurableVariants}
          />
      </div>
    </div>
  );
}
